/**
 * ClientPaymentsPanel
 * Reads from public.jphph_payment_schedules (UUID project_id).
 * Filters: project_id = projectId AND is_client_visible = true.
 * Ordered by due_date ascending.
 *
 * Columns used:
 *   id, project_id, title, description, amount, currency,
 *   due_date, status, client_confirmed, client_confirmed_at,
 *   management_confirmed, management_confirmed_at, is_client_visible,
 *   created_at, updated_at
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PaymentScheduleItem {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  amount: number;
  currency: string;
  due_date: string | null;
  status: string;
  client_confirmed: boolean;
  client_confirmed_at: string | null;
  management_confirmed: boolean;
  management_confirmed_at: string | null;
  is_client_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClientPaymentsPanelProps {
  /** Project UUID — used to query jphph_payment_schedules.project_id */
  projectId: string;
  /** Project code string — shown in header for context */
  projectCode?: string;
  /** Display name of the project */
  projectName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; icon: string; border: string }> = {
  paid:                 { label: 'Paid',               bg: '#F0FDF4', text: '#059669', icon: 'ri-checkbox-circle-fill',  border: '#BBF7D0' },
  management_confirmed: { label: 'Confirmed',          bg: '#F0FDF4', text: '#059669', icon: 'ri-checkbox-circle-fill',  border: '#BBF7D0' },
  client_confirmed:     { label: 'Awaiting Review',   bg: '#FFFBEB', text: '#F59E0B', icon: 'ri-loader-4-line',         border: '#FDE68A' },
  due:                  { label: 'Due Now',           bg: '#FEF0E7', text: '#E8621A', icon: 'ri-alarm-warning-line',    border: '#FDBA74' },
  overdue:              { label: 'Overdue',           bg: '#FEF2F2', text: '#EF4444', icon: 'ri-error-warning-fill',    border: '#FECACA' },
  upcoming:             { label: 'Upcoming',          bg: '#F9FAFB', text: '#9CA3AF', icon: 'ri-time-line',             border: '#E5E7EB' },
};

function fmtCurrency(amount: number, currency = 'NZD') {
  return new Intl.NumberFormat('en-NZ', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount);
}

function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysUntil(d: string | null): number | null {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due   = new Date(d);  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function urgencyBadge(status: string, due: string | null): { text: string; color: string } | null {
  if (status === 'paid' || status === 'management_confirmed' || status === 'client_confirmed') return null;
  const d = daysUntil(due);
  if (d === null) return null;
  if (d < 0)  return { text: `${Math.abs(d)} day${Math.abs(d) !== 1 ? 's' : ''} overdue`, color: '#EF4444' };
  if (d === 0) return { text: 'Due today', color: '#EF4444' };
  if (d <= 3)  return { text: `Due in ${d} day${d !== 1 ? 's' : ''}`, color: '#E8621A' };
  if (d <= 7)  return { text: `Due in ${d} days`, color: '#F59E0B' };
  return null;
}

function isPaidStatus(status: string) {
  return status === 'paid' || status === 'management_confirmed';
}

// ─── Main Panel ─────────────────────────────────────────────────────────────

export default function ClientPaymentsPanel({ projectId, projectCode, projectName }: ClientPaymentsPanelProps) {
  const { t } = useTranslation();
  const [payments, setPayments]     = useState<PaymentScheduleItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState('');
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');

    const { data, error } = await supabase
      .from('jphph_payment_schedules')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_client_visible', true)
      .order('due_date', { ascending: true });

    if (error) {
      console.error('[ClientPaymentsPanel] load error:', error);
      setLoadError(`Failed to load payment schedule: ${error.message}`);
    } else {
      setPayments((data as PaymentScheduleItem[]) ?? []);
    }

    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    if (projectId) loadData();
  }, [loadData, projectId]);

  async function handleConfirm(itemId: string) {
    setConfirmingId(itemId);

    const { error } = await supabase
      .from('jphph_payment_schedules')
      .update({
        client_confirmed: true,
        client_confirmed_at: new Date().toISOString(),
        status: 'client_confirmed',
        updated_at: new Date().toISOString(),
      })
      .eq('id', itemId);

    if (error) {
      console.error('[ClientPaymentsPanel] confirm error:', error);
      setLoadError(`Could not confirm payment: ${error.message}`);
      setConfirmingId(null);
      return;
    }

    // Optimistically update local state
    setPayments((prev) =>
      prev.map((p) =>
        p.id === itemId
          ? { ...p, client_confirmed: true, client_confirmed_at: new Date().toISOString(), status: 'client_confirmed' }
          : p
      )
    );
    setConfirmingId(null);
  }

  // Derived stats
  const total     = payments.reduce((s, p) => s + Number(p.amount), 0);
  const paidAmt   = payments.filter((p) => isPaidStatus(p.status)).reduce((s, p) => s + Number(p.amount), 0);
  const remaining = total - paidAmt;
  const overdueCount = payments.filter((p) => p.status === 'overdue').length;
  const dueCount     = payments.filter((p) => p.status === 'due').length;
  const pendingCount = payments.filter((p) => p.status === 'client_confirmed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
      </div>
    );
  }

  return (
    <>
      {/* Load error */}
      {loadError && (
        <div className="mb-4 flex items-start gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-2xl p-4">
          <i className="ri-error-warning-line text-[#EF4444] text-base mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="text-[#EF4444] font-semibold text-sm">{t('client.payments.failedToLoad')}</p>
            <p className="text-[#EF4444] text-xs mt-0.5">{loadError}</p>
          </div>
        </div>
      )}

      {/* ── Summary cards ── */}
      {payments.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-[#9CA3AF] text-xs mb-1">{t('client.payments.totalScheduled')}</p>
            <p className="text-[#1A1A1A] font-bold text-base">{fmtCurrency(total)}</p>
          </div>
          <div className="bg-[#F0FDF4] rounded-2xl border border-[#BBF7D0] p-4">
            <p className="text-[#059669] text-xs mb-1">{t('client.payments.paid')}</p>
            <p className="text-[#059669] font-bold text-base">{fmtCurrency(paidAmt)}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-[#9CA3AF] text-xs mb-1">{t('client.payments.remaining')}</p>
            <p className="text-[#1A1A1A] font-bold text-base">{fmtCurrency(remaining)}</p>
          </div>
          <div className={`rounded-2xl border p-4 ${
            overdueCount > 0 ? 'bg-[#FEF2F2] border-[#FECACA]' :
            dueCount > 0     ? 'bg-[#FEF0E7] border-[#FDBA74]' :
            pendingCount > 0 ? 'bg-[#FFFBEB] border-[#FDE68A]' :
            'bg-white border-gray-100'
          }`}>
            <p className={`text-xs mb-1 ${
              overdueCount > 0 ? 'text-[#EF4444]' :
              dueCount > 0     ? 'text-[#E8621A]' :
              pendingCount > 0 ? 'text-[#F59E0B]' :
              'text-[#9CA3AF]'
            }`}>
              {overdueCount > 0 ? t('client.payments.overdue') : dueCount > 0 ? t('client.payments.dueNow') : pendingCount > 0 ? t('client.payments.pending') : t('client.portal.status')}
            </p>
            <p className={`font-bold text-base ${
              overdueCount > 0 ? 'text-[#EF4444]' :
              dueCount > 0     ? 'text-[#E8621A]' :
              pendingCount > 0 ? 'text-[#F59E0B]' :
              'text-[#059669]'
            }`}>
              {overdueCount > 0 ? t('client.payments.paymentOverdue', { count: overdueCount, s: overdueCount !== 1 ? 's' : '' }) :
               dueCount > 0     ? t('client.payments.paymentDue', { count: dueCount, s: dueCount !== 1 ? 's' : '' }) :
               pendingCount > 0 ? `${pendingCount} ${t('client.payments.pending')}` :
               t('client.payments.onTrack')}
            </p>
          </div>
        </div>
      )}

      {/* ── Attention banner ── */}
      {(overdueCount > 0 || dueCount > 0) && (
        <div className={`rounded-2xl p-4 mb-5 flex items-start gap-3 ${
          overdueCount > 0 ? 'bg-[#FEF2F2] border border-[#FECACA]' : 'bg-[#FEF0E7] border border-[#FDBA74]'
        }`}>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
            overdueCount > 0 ? 'bg-[#EF4444]/10' : 'bg-[#E8621A]/10'
          }`}>
            <i className={`${overdueCount > 0 ? 'ri-error-warning-fill text-[#EF4444]' : 'ri-alarm-warning-line text-[#E8621A]'} text-lg`}></i>
          </div>
          <div>
            <p className={`font-bold text-sm ${overdueCount > 0 ? 'text-[#EF4444]' : 'text-[#E8621A]'}`}>
              {overdueCount > 0
                ? t('client.payments.paymentOverdue', { count: overdueCount, s: overdueCount !== 1 ? 's' : '' })
                : t('client.payments.paymentDue', { count: dueCount, s: dueCount !== 1 ? 's' : '' })}
            </p>
            <p className="text-[#374151] text-xs mt-0.5">
              {t('client.payments.makePayment')}
            </p>
          </div>
        </div>
      )}

      {/* ── Payment list ── */}
      {payments.length > 0 ? (
        <div className="space-y-3">
          {payments.map((payment, idx) => {
            const cfg   = STATUS_CONFIG[payment.status] ?? STATUS_CONFIG.upcoming;
            const badge = urgencyBadge(payment.status, payment.due_date);
            const showConfirm = !payment.client_confirmed && !isPaidStatus(payment.status);

            return (
              <div
                key={payment.id}
                className="bg-white rounded-2xl overflow-hidden border"
                style={{ borderColor: cfg.border }}
              >
                {/* Accent bar for urgent */}
                {(payment.status === 'due' || payment.status === 'overdue') && (
                  <div className="h-1" style={{ backgroundColor: cfg.text }}></div>
                )}

                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Step circle */}
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5"
                      style={{ backgroundColor: cfg.bg, color: cfg.text }}
                    >
                      {isPaidStatus(payment.status)
                        ? <i className="ri-check-line text-sm"></i>
                        : idx + 1}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Title + status badge */}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-[#1A1A1A] font-bold text-sm leading-snug">{payment.title}</h4>
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap flex items-center gap-1"
                          style={{ backgroundColor: cfg.bg, color: cfg.text }}
                        >
                          <i className={`${cfg.icon} text-[10px]`}></i>
                          {cfg.label}
                        </span>
                      </div>

                      {/* Description */}
                      {payment.description && (
                        <p className="text-[#9CA3AF] text-xs leading-relaxed mb-2">{payment.description}</p>
                      )}

                      {/* Amount / due date row */}
                      <div className="flex items-center gap-4 flex-wrap">
                        <div>
                          <p className="text-[#9CA3AF] text-[10px]">{t('client.payments.amount')}</p>
                          <p className="text-[#1A1A1A] font-bold text-base">{fmtCurrency(Number(payment.amount), payment.currency)}</p>
                        </div>
                        {payment.due_date && (
                          <div>
                            <p className="text-[#9CA3AF] text-[10px]">{t('client.payments.dueDate')}</p>
                            <p className={`font-semibold text-sm ${
                              payment.status === 'overdue' ? 'text-[#EF4444]' :
                              payment.status === 'due'     ? 'text-[#E8621A]' :
                              'text-[#374151]'
                            }`}>{fmtDate(payment.due_date)}</p>
                          </div>
                        )}
                      </div>

                      {/* Urgency badge */}
                      {badge && (
                        <div
                          className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: `${badge.color}18` }}
                        >
                          <i className="ri-alarm-warning-line text-xs" style={{ color: badge.color }}></i>
                          <span className="text-xs font-bold" style={{ color: badge.color }}>{badge.text}</span>
                        </div>
                      )}

                      {/* Client confirmed notice */}
                      {payment.client_confirmed && (
                        <div className="mt-3 flex items-center gap-2 bg-[#F0FDF4] rounded-xl px-3 py-2.5">
                          <i className="ri-checkbox-circle-fill text-[#059669] text-sm flex-shrink-0"></i>
                          <p className="text-[#059669] text-xs font-semibold">
                            {t('client.payments.youConfirmed')}
                            {payment.client_confirmed_at ? ` · ${fmtDate(payment.client_confirmed_at)}` : ''}
                          </p>
                        </div>
                      )}

                      {/* Management confirmed notice */}
                      {payment.management_confirmed && (
                        <div className="mt-2 flex items-center gap-2 bg-[#F0FDF4] rounded-xl px-3 py-2.5">
                          <i className="ri-shield-check-line text-[#059669] text-sm flex-shrink-0"></i>
                          <p className="text-[#059669] text-xs font-semibold">
                            {t('client.payments.confirmedByJH')}
                            {payment.management_confirmed_at ? ` · ${fmtDate(payment.management_confirmed_at)}` : ''}
                          </p>
                        </div>
                      )}

                      {/* Confirm Payment button */}
                      {showConfirm && (
                        <button
                          onClick={() => handleConfirm(payment.id)}
                          disabled={confirmingId === payment.id}
                          className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#059669] text-white text-sm font-bold cursor-pointer disabled:opacity-60 whitespace-nowrap hover:bg-[#047857] transition-colors"
                        >
                          {confirmingId === payment.id ? (
                            <><i className="ri-loader-4-line animate-spin"></i>{t('client.payments.confirming')}</>
                          ) : (
                            <><i className="ri-checkbox-circle-line text-base"></i>{t('client.payments.confirmPayment')}</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty state */
        <div className="text-center py-16">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-bank-card-line text-5xl"></i>
          </div>
          <p className="text-[#374151] font-semibold mb-1">{t('client.payments.noPayments')}</p>
          <p className="text-[#9CA3AF] text-sm">{t('client.payments.noPaymentsDesc')}</p>
        </div>
      )}

      {/* Footer note */}
      {payments.length > 0 && (
        <div className="mt-5 bg-[#F9FAFB] rounded-2xl p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#9CA3AF] text-base mt-0.5 flex-shrink-0"></i>
          <p className="text-[#9CA3AF] text-xs leading-relaxed">
            {t('client.payments.gstNote', { currency: payments[0]?.currency || 'NZD' })}
          </p>
        </div>
      )}
    </>
  );
}